import React, { ReactElement } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { featureFlags } from '../../config/featureFlags';
import '../../styles/sidebar.css';
import RoleGuard from '../../shared/guards/RoleGuard.js';

interface NavItem {
  label: string;
  path: string;
  icon?: ReactElement;
  tooltip?: string;
  sectionSeparator?: string; // Section label for visual grouping (e.g., "MONITORING", "INVESTIGATION")
  roles?: string[];
  children?: {
    label: string;
    path: string;
    icon?: ReactElement;
    tooltip?: string;
    roles?: string[];
    children?: {
      label: string;
      path: string;
      icon?: ReactElement;
      tooltip?: string;
      roles?: string[];
      children?: {
        label: string;
        path: string;
        icon?: ReactElement;
        tooltip?: string;
        roles?: string[];
      }[];
    }[];
  }[];
}

// B10 NAVIGATION AUDIT (2026-02-14)
// Active sections: MONITORING, AUTOMATION (B7), CONFIGURATION, GOVERNANCE, ADMINISTRATION, SUPPORT
// Deprecated: Investigation Notebook (B6), Rules Management (B10), Graph Explorer (soft-deprecated)
// Section order follows analyst workflow: Monitor → Automate → Investigate (ITSM) → Configure → Govern → Admin
const navItems: NavItem[] = [
  {
    label: 'Core Dashboards',
    path: '/dashboards',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
    tooltip: 'Start here - Monitor alerts and autonomous decisions',
    sectionSeparator: 'MONITORING',
    roles: ['admin', 'analyst', 'executive'],
    children: [
      { label: 'Analyst View', path: '/analyst', tooltip: 'Daily alert triage - Click alerts to investigate suspicious activity', roles: ['admin', 'analyst'] },
      { label: 'Executive View', path: '/executive', tooltip: 'Leadership overview - Risk metrics, SLA performance, and compliance status', roles: ['admin', 'executive'] },
      { label: 'Threat View', path: '/threat-view', tooltip: 'Pentest operations — engagements, findings, exploitation, compliance', roles: ['admin', 'analyst'] }
    ]
  },
  // B6 DEPRECATED: Investigation Notebook — contradicts autonomous decision engine vision.
  // BRAIN creates incidents in external ITSM (ServiceNow, Jira) and tracks them in Work Queue.
  // Route /investigation kept for backward compatibility but hidden from nav.
  {
    label: 'Work Queue',
    path: '/tickets',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    tooltip: 'Track external tickets - View Jira/ServiceNow tickets for your alerts',
    roles: ['admin', 'analyst']
  },
  {
    label: 'User Behavior',
    path: '/user-behavior',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    tooltip: 'Analyze suspicious users → View anomalies → Detect insider threats and compromised accounts',
    roles: ['admin', 'analyst']
  },
  // SOFT DEPRECATED 2026-01-02: Hidden for 180 days unless requested
  // To enable: localStorage.setItem('ENABLE_GRAPH_EXPLORER', 'true')
  ...(featureFlags.enableGraphExplorer ? [{
    label: 'Graph Explorer',
    path: '/graph',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    ),
    tooltip: 'Visualize attack paths and entity relationships',
    roles: ['admin', 'analyst'],
    children: [
      { label: 'Graph Canvas', path: '/graph', tooltip: 'Interactive visualization - Drag nodes, explore connections, trace attack paths', roles: ['admin', 'analyst'] },
      { label: 'Graph Explorer', path: '/graph-explorer', tooltip: 'Search entities - Find users, hosts, IPs and their relationships', roles: ['admin', 'analyst'] },
      { label: 'Query Builder', path: '/graph-query', tooltip: 'Build custom queries - Advanced graph pattern matching for threat hunting', roles: ['admin', 'analyst'] }
    ]
  }] : []),
  {
    label: 'Playbooks',
    path: '/playbooks',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
    tooltip: 'Review AI-suggested playbooks — Approve, refine, or reject automation drafts',
    sectionSeparator: 'AUTOMATION',
    roles: ['admin', 'analyst']
  },
  {
    label: 'Decision Policies',
    path: '/decision-policies',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
      </svg>
    ),
    tooltip: 'Manage decision policies — Configure rules that reclassify alerts after BRAIN\'s Decision Engine',
    roles: ['admin', 'analyst']
  },
  {
    label: 'Threat Intelligence',
    path: '/threat-intelligence',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    tooltip: 'Explore threat data and actor profiles',
    roles: ['admin', 'analyst'],
    children: [
      { label: 'Threat Explorer', path: '/threat-intelligence', tooltip: 'Browse threat feeds - IOCs, TTPs, and threat actor campaigns', roles: ['admin', 'analyst'] },
      { label: 'Threat Actor Profiles', path: '/threat-intelligence/actors', tooltip: 'View threat actors - APT groups, motivations, and known TTPs', roles: ['admin', 'analyst'] },
      { label: 'Hunting Workbench', path: '/threat-intelligence/hunting', tooltip: 'Proactive threat hunting - Search for hidden threats using threat intelligence', roles: ['admin', 'analyst'] }
    ]
  },
  {
    label: 'Integration Hub',
    path: '/integration-hub',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    ),
    tooltip: 'Connect data sources and configure threat feeds',
    sectionSeparator: 'CONFIGURATION',
    roles: ['admin', 'analyst'],
    children: [
      { label: 'Data Connectors', path: '/integration-hub/data-connectors', tooltip: 'Add SIEM, EDR, cloud sources - Configure data ingestion from security tools', roles: ['admin'] },
      { label: 'Response Actions', path: '/integration-hub/response-actions', tooltip: 'Configure SOAR response connectors - CrowdStrike, Palo Alto, Azure AD, ServiceNow and more', roles: ['admin'] },
      { label: 'Diagram Uploads', path: '/integration-hub/diagram-uploads', tooltip: 'Upload network diagrams - Provide context for asset relationships', roles: ['admin', 'analyst'] },
      // DISABLED: Business Context Mapper - Backend endpoints not implemented (see MASTER-PRIORITY-LIST.md - OPT-05)
      // { label: 'Business Context Mapper', path: '/integration-hub/business-context-mapper', tooltip: 'Map technical assets to business context', roles: ['admin'] },
      { label: 'Threat Intel Feeds', path: '/integration-hub/threat-intel-feeds', tooltip: 'Configure threat feeds - Add STIX/TAXII sources for enrichment', roles: ['admin', 'analyst'] },
      // REMOVED: Connector Health - Duplicate of Health Monitoring tab in ConnectorDetail (LESSON 1: Single source of truth)
      // { label: 'Connector Health', path: '/integration-hub/connector-health', tooltip: 'Monitor connector status and health', roles: ['admin', 'analyst'] },
      { label: 'Integration Metrics', path: '/integration-hub/integration-metrics', tooltip: 'Monitor connector health - View ingestion rates and error logs', roles: ['admin', 'analyst'] }
    ]
  },
  {
    label: 'API Management',
    path: '/api',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4M6 16l-4-4 4-4" />
      </svg>
    ),
    tooltip: 'Manage API keys and webhook integrations',
    roles: ['admin'],
    children: [
      { label: 'API Keys', path: '/api/keys', tooltip: 'Generate API keys - Create credentials for external integrations', roles: ['admin'] },
      { label: 'Usage & Quotas', path: '/api/usage', tooltip: 'Monitor API usage - Track rate limits and quota consumption', roles: ['admin'] },
      { label: 'API Reference', path: '/api/docs', tooltip: 'Interactive API docs - Test endpoints and view examples', roles: ['admin'] },
      { label: 'Webhook Settings', path: '/api/webhooks', tooltip: 'Configure webhooks - Send alerts to external systems (Slack, Teams, PagerDuty)', roles: ['admin'] },
      { label: 'Source Registry', path: '/api/sources', tooltip: 'Track data sources - View all configured connectors and their status', roles: ['admin'] }
    ]
  },
  {
    label: 'Compliance',
    path: '/compliance',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    tooltip: 'Track compliance status and manage audits',
    sectionSeparator: 'GOVERNANCE',
    roles: ['admin', 'analyst', 'executive'],
    children: [
      { label: 'Compliance Dashboard', path: '/compliance', tooltip: 'View compliance posture - SOC2, PCI-DSS, HIPAA, GDPR status', roles: ['admin', 'analyst', 'executive'] },
      { label: 'Asset Tagging', path: '/compliance/asset-tagging', tooltip: 'Tag CMDB-imported assets with compliance frameworks (PCI, HIPAA, SOC2, etc.) — prerequisite for per-framework breakdown', roles: ['admin', 'analyst'] },
      { label: 'Compliance Reports', path: '/compliance/reports', tooltip: 'Schedule + download framework-mapped compliance reports (SOC 2 evidence, PCI DSS, HIPAA, etc.)', roles: ['admin', 'analyst'] },
      { label: 'Control Mapping', path: '/compliance/controls', tooltip: 'Map BRAIN actions to compliance framework control numbers — drives gap analysis and framework reports', roles: ['admin', 'analyst'] },
      { label: 'Audit Programs', path: '/compliance/audits', tooltip: 'Schedule + track audit programs (SOC 2, PCI, HIPAA) — evidence, findings, remediation. Replaces older Audit Management.', roles: ['admin', 'analyst'] },
      { label: 'External Auditors', path: '/compliance/auditors', tooltip: 'Invite + manage external auditors (Deloitte, EY, KPMG) for SOC 2 / PCI / HIPAA attestation cycles', roles: ['admin'] },
      { label: 'Audit Management', path: '/audit-management', tooltip: 'Manage audit schedules, evidence repository, findings, and compliance reports', roles: ['admin', 'analyst'] },
      { label: 'Platform Audit Log', path: '/platform-audit-log', tooltip: 'SOAR action audit trail - Execution logs, approvals, system events, and action readiness', roles: ['admin', 'analyst'] }
    ]
  },
  {
    label: 'Agent Registry',
    path: '/agent-registry',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
    tooltip: 'Manage autonomous agents and approve deployments',
    roles: ['admin', 'analyst'],
  },
  {
    label: 'Regulatory Notifications',
    path: '/regulatory-notifications',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    tooltip: 'AI governance regulatory notifications and compliance reporting',
    roles: ['admin', 'analyst'],
  },
  // B10 DEPRECATED: Rules Management — legacy SIEM paradigm, contradicts autonomous vision.
  // Autonomous BRAIN uses ML/AI (ensemble, GNN, LLM, RL) not manual rules.
  // Future: Transform to Policy-as-Code for autonomous guardrails.
  {
    label: 'Advanced Features',
    path: '/advanced',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    tooltip: 'ML feedback and advanced capabilities',
    roles: ['admin', 'analyst'],
    children: [
      {
        label: 'Analyst Feedback',
        path: '/advanced/ml/analyst-feedback',
        tooltip: 'Train ML models - Mark alerts as TP/FP/FN to improve detection accuracy',
        roles: ['admin', 'analyst']
      }
      // ML Infrastructure removed - Now in Platform Admin only
      // Removed: Inference Pipeline, Training Dashboard, Model Registry, Performance Analytics,
      //          Model Explainability, Feedback Analytics, All Feedback Sources, Hyperparameter Tuning, Log Explorer
      // Reason: $59M/year risk - Model poisoning, data exfiltration, compliance violations, support burden
      // Security: Tenant admins with ML access can break models, expose data, game feedback system
      // Location: admin.astrapeak.ai → ML Infrastructure
      // Kept: Analyst Feedback (core workflow, low risk, necessary for ML learning)
    ]
  },
  {
    label: 'Administration',
    path: '/admin',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    tooltip: 'Manage users, roles, and tenant settings',
    sectionSeparator: 'ADMINISTRATION',
    roles: ['admin'],
    children: [
      {
        label: 'User Management',
        path: '/admin/users',
        tooltip: 'Add/remove users - Manage accounts and access control',
        roles: ['admin']
      },
      // {
      //   label: 'Role Management',
      //   path: '/admin/roles',
      //   tooltip: 'Define RBAC roles - Configure permissions and scopes',
      //   roles: ['admin']
      // },
      {
        label: 'Tenant Settings',
        path: '/admin/tenant',
        tooltip: 'Configure tenant - Branding, quotas, and default views',
        roles: ['admin']
      },
      {
        label: 'LLM Settings',
        path: '/admin/llm-settings',
        tooltip: 'Configure AI provider, API key, model, and view usage',
        roles: ['admin']
      }
      // System Configuration removed - Now in Platform Admin only
      // Reason: Affects ALL tenants, controls ML training globally
      // Location: admin.astrapeak.ai → System Configuration
    ]
  },
  {
    label: 'Support & Help',
    path: '/support',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    tooltip: 'Get help and access documentation',
    roles: ['admin', 'analyst', 'executive'],
    children: [
      { label: 'Help Center', path: '/support/help-center', tooltip: 'Get support - Create tickets and contact support team', roles: ['admin', 'analyst', 'executive'] }
      // HIDDEN: Product Documentation - Temporarily disabled per requirement
      // { label: 'Product Documentation', path: '/support/documentation', tooltip: 'Browse docs - User guides, API references, and tutorials', roles: ['admin', 'analyst', 'executive'] }
    ]
  },
];

interface SideNavProps {
  isOpen: boolean;
}

export const SideNav: React.FC<SideNavProps> = ({ isOpen }) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  React.useEffect(() => {
    // Find active parent, child, grandchild, and great-grandchild items
    const activeParent = navItems.find(item =>
      location.pathname.startsWith(item.path)
    );

    // Always auto-expand the Administration section for admin users
    const alwaysExpanded = ['/admin'];

    if (activeParent) {
      const pathsToExpand: string[] = [activeParent.path];

      // Find active child
      const activeChild = activeParent.children?.find(child =>
        location.pathname.startsWith(child.path) ||
        child.children?.some(grandchild =>
          location.pathname.startsWith(grandchild.path) ||
          grandchild.children?.some(greatGrandchild =>
            location.pathname.startsWith(greatGrandchild.path)
          )
        )
      );

      if (activeChild) {
        pathsToExpand.push(activeChild.path);

        // Find active grandchild
        const activeGrandchild = activeChild.children?.find(grandchild =>
          location.pathname.startsWith(grandchild.path) ||
          grandchild.children?.some(greatGrandchild =>
            location.pathname.startsWith(greatGrandchild.path)
          )
        );

        if (activeGrandchild) {
          pathsToExpand.push(activeGrandchild.path);
        }
      }

      // Merge always-expanded paths with active paths
      const merged = [...new Set([...pathsToExpand, ...alwaysExpanded])];
      setExpandedItems(merged);
    } else {
      // Even when no active parent matches, keep Administration expanded
      setExpandedItems(alwaysExpanded);
    }
  }, [location]);

  const toggleItem = (path: string) => {
    setExpandedItems(prev =>
      prev.includes(path)
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  return (
    <nav className={`fixed left-0 top-0 h-full bg-gray-900 text-white shadow-lg overflow-y-auto transition-all duration-300 z-40 ${isOpen ? 'w-64' : 'w-0'
      }`}>
      <div className="py-4 mt-16">
        {navItems.map((item, index) => (
          <RoleGuard key={item.path} allowedRoles={item.roles || []}>
            <div className="mb-2">
              {/* Section separator */}
              {item.sectionSeparator && (
                <div
                  className={`px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider text-center ${index > 0 ? 'border-t border-gray-700 mt-4' : ''
                    }`}
                >
                  {item.sectionSeparator}
                </div>
              )}

              {item.children ? (
                <button
                  onClick={() => toggleItem(item.path)}
                  className={`flex items-center w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors ${location.pathname.includes(item.path) ? 'bg-gray-800' : ''}`}
                  aria-expanded={expandedItems.includes(item.path)}
                  title={item.tooltip}
                >
                  {item.icon && <span className="mr-3">{item.icon}</span>}
                  <span className="flex-grow">{item.label}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${expandedItems.includes(item.path) ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              ) : (
                <NavLink
                  to={item.path}
                  className={`flex items-center w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors ${location.pathname.includes(item.path) ? 'bg-gray-800' : ''}`}
                  title={item.tooltip}
                >
                  {item.icon && <span className="mr-3">{item.icon}</span>}
                  <span className="flex-grow">{item.label}</span>
                </NavLink>
              )}
              {expandedItems.includes(item.path) && item.children && (
                <div className="mt-1 ml-6 space-y-1">
                  {item.children.map((child) => (
                    <RoleGuard key={child.path} allowedRoles={child.roles || []}>
                      <div>
                        {child.children ? (
                          <>
                            <button
                              onClick={() => toggleItem(child.path)}
                              className={`flex items-center w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors ${location.pathname.includes(child.path) ? 'bg-gray-800' : ''}`}
                              aria-expanded={expandedItems.includes(child.path)}
                              title={child.tooltip}
                            >
                              <span className="flex-grow">{child.label}</span>
                              <svg
                                className={`w-4 h-4 transition-transform ${expandedItems.includes(child.path) ? 'transform rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {expandedItems.includes(child.path) && child.children && (
                              <div className="mt-1 ml-6 space-y-1">
                                {child.children.map((grandchild) => (
                                  <RoleGuard key={grandchild.path} allowedRoles={grandchild.roles || []}>
                                    <div>
                                      {grandchild.children ? (
                                        <>
                                          <button
                                            onClick={() => toggleItem(grandchild.path)}
                                            className={`flex items-center w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors ${location.pathname.includes(grandchild.path) ? 'bg-gray-800' : ''}`}
                                            aria-expanded={expandedItems.includes(grandchild.path)}
                                            title={grandchild.tooltip}
                                          >
                                            <span className="flex-grow">{grandchild.label}</span>
                                            <svg
                                              className={`w-4 h-4 transition-transform ${expandedItems.includes(grandchild.path) ? 'transform rotate-180' : ''}`}
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                          </button>
                                          {expandedItems.includes(grandchild.path) && grandchild.children && (
                                            <div className="mt-1 ml-6 space-y-1">
                                              {grandchild.children.map((greatGrandchild) => (
                                                <RoleGuard key={greatGrandchild.path} allowedRoles={greatGrandchild.roles || []}>
                                                  <NavLink
                                                    to={greatGrandchild.path}
                                                    className={({ isActive }) =>
                                                      `block px-4 py-2 hover:bg-gray-800 transition-colors ${isActive ? 'bg-gray-800 text-blue-400' : ''}`
                                                    }
                                                    title={greatGrandchild.tooltip}
                                                  >
                                                    {greatGrandchild.label}
                                                  </NavLink>
                                                </RoleGuard>
                                              ))}
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <NavLink
                                          to={grandchild.path}
                                          className={({ isActive }) =>
                                            `block px-4 py-2 hover:bg-gray-800 transition-colors ${isActive ? 'bg-gray-800 text-blue-400' : ''}`
                                          }
                                          title={grandchild.tooltip}
                                        >
                                          {grandchild.label}
                                        </NavLink>
                                      )}
                                    </div>
                                  </RoleGuard>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <NavLink
                            to={child.path}
                            className={({ isActive }) =>
                              `block px-4 py-2 hover:bg-gray-800 transition-colors ${isActive ? 'bg-gray-800 text-blue-400' : ''}`
                            }
                            title={child.tooltip}
                          >
                            {child.label}
                          </NavLink>
                        )}
                      </div>
                    </RoleGuard>
                  ))}
                </div>
              )}
            </div>
          </RoleGuard>
        ))}
      </div>
    </nav>
  );
};
